import type { Block } from "../contract"
import type { TurnView } from "../store"
import { contextParts, idx, walkBlocks } from "./format"
import { authoritiesText } from "./authorities"

function blockLines(b: Block): string[] {
  switch (b.type) {
    case "para":
      return [b.text + (b.cites?.length ? ` [${b.cites.map((c) => c.n).join(", ")}]` : "")]
    case "quote":
      return [`"${[b.before, b.phrase, b.after].filter(Boolean).join(" ")}"`]
    case "list":
      return b.items.map((t, i) => (b.ordered ? `${i + 1}. ${t}` : `- ${t}`))
    case "checklist":
      return b.items.map((t) => `- ${t}`)
    case "table":
      return b.rows.map(([k, v]) => `${k}: ${v}`).concat(b.note ? [b.note] : [])
    case "note":
      return [b.text]
    case "tally":
      return [`Across ${b.total} decisions: won ${b.won}, sent back ${b.remanded}, lost ${b.lost}.`]
    case "your_use":
      return [
        ...(b.for.length ? [`If you rely on it: ${b.for.join("; ")}`] : []),
        ...(b.against.length ? [`If it is used against you: ${b.against.join("; ")}`] : []),
      ]
    default:
      return []
  }
}

/** The answer as plain text for the clipboard: question, verdict, matter, sections in order, sources, the line. */
export function answerText(view: TurnView): string {
  const out: string[] = []
  if (view.question) out.push(`Q: ${view.question}`, "")
  if (view.head) {
    out.push(view.head.short)
    if (view.head.lede) out.push(view.head.lede)
    const parts = contextParts(view.head.context)
    if (parts.length) out.push(`Matter: ${parts.join(" · ")}`)
    if (view.head.assumptions?.length) out.push(`Assumed: ${view.head.assumptions.join("; ")}`)
    out.push("")
  }
  ;(view.plan ?? []).forEach((p, i) => {
    const sec = view.sections[p.id]
    if (!sec) return
    out.push(`${idx(i)} · ${p.heading}`, p.takeaway)
    walkBlocks(sec.blocks, (b) => {
      if (b.type !== "disclosure") out.push(...blockLines(b))
    })
    out.push("")
  })
  const auth = authoritiesText(view)
  if (auth.length) out.push(...auth, "")
  out.push("Research, not legal advice. Check the cited text before you rely on it.")
  return out.join("\n")
}
